import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  PLATFORM_ID,
} from '@angular/core';
import { ConnectionStatus, WebSocketService } from '@freelancer/datastore';
import { TimeUtils } from '@freelancer/time-utils';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import { FontColor, TextSize } from '@freelancer/ui/text';
import type { Observable } from 'rxjs';
import { of, Subscription } from 'rxjs';
import { map, mergeMap, startWith } from 'rxjs/operators';

const ONLINE_DISPLAY_DURATION = 1000;

@Component({
  selector: 'fl-network-banner',
  template: `
    <div
      *ngIf="!(hideBanner$ | flAsync)"
      class="NetworkBanner"
    >
      <fl-icon
        [name]="'ui-info-v2'"
        [color]="
          (websocketStatus$ | flAsync) === ConnectionStatus.CLOSED
            ? IconColor.ERROR
            : IconColor.SUCCESS
        "
        [size]="IconSize.XSMALL"
        [flMarginRight]="'xxxsmall'"
      ></fl-icon>
      <ng-container [ngSwitch]="websocketStatus$ | flAsync">
        <ng-container *ngSwitchCase="ConnectionStatus.CLOSED">
          <div *ngIf="hasTimePassed; else reconnect">
            <p
              class="text-color-inherit text-xsmall"
              i18n="
                Network banner text to notify the user trying to connect to chat
              "
            >
              Connecting to chat...
            </p>
          </div>
        </ng-container>
        <ng-container *ngSwitchCase="ConnectionStatus.OPEN">
          <p
            class="text-color-inherit text-xsmall"
            i18n="Network banner text to notify the user is back online"
          >
            You are back online
          </p>
        </ng-container>
      </ng-container>
      <ng-template #reconnect>
        <p
          class="text-color-inherit text-xsmall"
          i18n="
            Network banner text to notify the user will retry connecting to
            internet
          "
        >
          Unable to connect to chat, retrying&nbsp;
        </p>
        <fl-relative-time
          [date]="date"
          [size]="TextSize.XSMALL"
          [color]="FontColor.INHERIT"
          [strict]="true"
          (timePassed)="showTimePassedText()"
        ></fl-relative-time>
      </ng-template>
    </div>
  `,
  styleUrls: ['./network-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NetworkBannerComponent implements OnInit, OnDestroy {
  IconColor = IconColor;
  IconSize = IconSize;
  ConnectionStatus = ConnectionStatus;
  TextSize = TextSize;
  FontColor = FontColor;

  hideBanner$: Observable<boolean>;
  websocketStatus$: Observable<ConnectionStatus>;
  subscriptions = new Subscription();
  hasTimePassed = false;
  date: Date;

  constructor(
    private websocket: WebSocketService,
    private timeUtils: TimeUtils,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.websocketStatus$ = this.websocket
        .getConnectStatusForUser()
        .pipe(map(bundle => bundle.status));
      this.hideBanner$ = this.websocketStatus$.pipe(
        mergeMap(status => {
          if (status === ConnectionStatus.CLOSED) {
            return of(false);
          }
          return of(true).pipe(this.timeUtils.rxDelay(ONLINE_DISPLAY_DURATION));
        }),
        startWith(true),
      );

      this.subscriptions.add(
        this.websocket.getConnectStatusForUser().subscribe(detail => {
          if (
            detail.status === ConnectionStatus.CLOSED &&
            detail.retryDelayInSeconds > 0
          ) {
            this.date = new Date(
              Date.now() + detail.retryDelayInSeconds * 1000,
            );
          }
          this.hasTimePassed = false;
        }),
      );
    }
  }

  showTimePassedText(): void {
    this.hasTimePassed = true;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
